
// @ts-ignore
import Highchart from '@/components/highchart-new';
import { fetchHistoricalRates } from '@/sdk/historical-rates';

// --- NEW TIME CONSTANTS AND INTERVAL DEFINITIONS ---
// Define basic time units in milliseconds
const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

// Use average year (365.25 days) for consistency across time zones and years
const MS_PER_YEAR_AVG = 365.25 * MS_PER_DAY;
const MS_PER_MONTH_AVG = MS_PER_YEAR_AVG / 12;

// Define the intervals as simple millisecond numbers (durations)
const INTERVAL_SIX_MONTHS = Math.round(6 * MS_PER_MONTH_AVG);
const INTERVAL_ONE_YEAR = Math.round(MS_PER_YEAR_AVG);
const INTERVAL_FIVE_YEARS = Math.round(5 * MS_PER_YEAR_AVG);
const INTERVAL_TEN_YEARS = Math.round(10 * MS_PER_YEAR_AVG);
// --- END NEW TIME CONSTANTS ---

// The cache object should explicitly allow string keys.
const ratesForInterval: { [key: string]: any } = {};

/**
 * The interval argument can be a number (millisecond duration) or null (for 'all').
 * We use a valid string key for the cache when the interval is null.
 */
async function memoizedFetchHistoricalRates(interval: number | null) {
  // 1. Determine the cache key: use the number itself, or "all" if it's null.
  const cacheKey = interval === null ? 'all' : String(interval);

  // 2. Use the valid string key to access the cache object.
  ratesForInterval[cacheKey] =
    ratesForInterval[cacheKey] || (await fetchHistoricalRates(interval));

  // 3. Return the cached result.
  return ratesForInterval[cacheKey];
}

// REMOVED: function interval(transformStartOfTime) {...}
// REMOVED: const sixMonths = interval((d) => d.setMonth(6));

export default {
  name: 'InterestRateWidget',
  components: {
    HHighchart: Highchart,
  },
  data() {
    return {
      // Initialize with the new millisecond constant
      interval: INTERVAL_SIX_MONTHS,
      enabledSeries: ['rates10years', 'rates15years'],
      ratesData: {
        rates5years: [],
        rates10years: [],
        rates15years: [],
        rates20years: [],
        bunds10yYields: [],
      },
    };
  },
  computed: {
    intervals() {
      // The intervals computed property now returns simple numbers (milliseconds)
      return {
        sixMonths: INTERVAL_SIX_MONTHS,
        oneYear: INTERVAL_ONE_YEAR,
        fiveYears: INTERVAL_FIVE_YEARS,
        tenYears: INTERVAL_TEN_YEARS,
        all: null,
      };
    },
    seriesNames() {
      return Object.keys(this.ratesData);
    },
    updateArgs() {
      return [
        'options.title',
        'options.series',
        'options.yAxis',
        'options.xAxis',
      ];
    },
    intervalOptions() {
      return Object.entries(this.intervals).map(([k, v]) => ({
        value: v,
        text: this.$t(k),
      }));
    },
    yAxisFormatter() {
      return new Intl.NumberFormat('de-DE', {
        style: 'percent',
        maximumSignificantDigits: 3,
      });
    },
    chartOptions() {
      const vueContext = this;
      return {
        chart: {
          type: 'line',
          style: {
            fontFamily: 'TTNorms',
            fontSize: 12,
          },
        },
        responsive: {
          rules: [
            {
              condition: {
                maxWidth: 500,
              },
              chartOptions: {
                legend: {
                  align: 'center',
                  verticalAlign: 'bottom',
                  layout: 'horizontal',
                },
                subtitle: {
                  text: null,
                },
                credits: {
                  enabled: false,
                },
              },
            },
          ],
        },
        tooltip: {
          shadow: false,
          enabled: true,
          useHTML: true,
          formatter() {
            return `
<div class="font-weight-medium white--text interest-tooltip" style="background-color: ${
              this.color
            };">
${vueContext.formatDataTooltip(this.y)}
</div>
`;
          },
        },
        title: {
          text: undefined,
        },
        xAxis: {
          crosshair: true,
          type: 'datetime',
          startOnTick: false,
          lineColor: this.$colors.grey.lighten1,
          lineWidth: 1.5,
          tickColor: this.$colors.grey.lighten1,
          minorTickColor: this.$colors.grey.lighten1, // *** UPDATED LOGIC HERE *** // The interval is now a number (milliseconds), so we remove the .getTime() call.
          min: this.interval
            ? Date.now() - this.interval // this.interval is already in milliseconds
            : new Date('1999-7-1').getTime(), // *** END UPDATED LOGIC ***
          labels: {
            style: {
              fontWeight: 'normal',
              fontSize: vueContext.$vuetify.breakpoint.xsOnly ? '10px' : '14px',
              color: '#0D324D',
            },
          },
        },
        yAxis: {
          lineColor: this.$colors.grey.lighten1,
          lineWidth: 1,
          labels: {
            step: 1,
            reserveSpace: true,
            style: {
              fontWeight: 'normal',
              fontSize: vueContext.$vuetify.breakpoint.xsOnly ? '10px' : '14px',
              color: '#0D324D',
            },
            formatter() {
              return vueContext.yAxisFormatter.format(this.value);
            },
          },
          tickColor: this.$colors.grey.lighten1,
          minorTickColor: this.$colors.grey.lighten1,
          title: {
            text: undefined,
          },
        },
        credits: {
          enabled: false,
        },
        legend: {
          enabled: false,
        },
        plotOptions: {
          series: {
            marker: {
              enabled: false,
            },
          },
          line: {
            allowOverlap: true,
          },
        },

        series: [
          {
            type: 'line',
            name: '5 years',
            data: this.ratesData.rates5years,
            visible: this.isSeriesEnabled('rates5years'),
            color: this.getSeriesColor('rates5years'),
          },
          {
            type: 'line',
            name: '10 years',
            data: this.ratesData.rates10years,
            visible: this.isSeriesEnabled('rates10years'),
            color: this.getSeriesColor('rates10years'),
          },
          {
            type: 'line',
            name: '15 years',
            data: this.ratesData.rates15years,
            visible: this.isSeriesEnabled('rates15years'),
            color: this.getSeriesColor('rates15years'),
          },
          {
            type: 'line',
            name: '20 years',
            data: this.ratesData.rates20years,
            visible: this.isSeriesEnabled('rates20years'),
            color: this.getSeriesColor('rates20years'),
          },
          {
            type: 'line',
            name: 'Germany 10 years bonds',
            data: this.ratesData.bunds10yYields,
            visible: this.isSeriesEnabled('bunds10yYields'),
            color: this.getSeriesColor('bunds10yYields'),
          },
        ],
      };
    },
  },
  async mounted() {
    await this.fetchRates();
    this.$emit('chartMounted');
  },
  methods: {
    seriesDisabled(seriesName) {
      return (
        (this.enabledSeries.length === 1 &&
          this.enabledSeries[0] === seriesName) ||
        (this.isSeriesEnabled('bunds10yYields') &&
          ['rates5years', 'rates15years', 'rates20years'].includes(seriesName))
      );
    },
    async fetchRates() {
      // this.interval is already the millisecond duration
      const rates = await memoizedFetchHistoricalRates(this.interval);
      this.ratesData = this.ratesToRatesData(rates);
      this.$forceUpdate();
    },
    isSeriesEnabled(seriesName) {
      return this.enabledSeries.includes(seriesName);
    },
    onFixationSelected(seriesName, val) {
      if (seriesName === 'bunds10yYields' && val) {
        this.enabledSeries = ['rates10years'];
      }
      if (val) {
        this.enabledSeries = [...this.enabledSeries, seriesName];
      } else {
        this.enabledSeries = this.enabledSeries.filter((e) => e !== seriesName);
      }
    },
    async handleIntervalChange(value) {
      // value is the millisecond number from the intervalOptions array
      this.interval = value;
      await this.fetchRates();
    },
    ratesToRatesData(rates) {
      const seriesNames = this.seriesNames;
      function mapSeries(series) {
        return series.map((sample) => [Date.parse(sample.time), sample.value]);
      }
      return seriesNames.reduce(
        (result, seriesName) => ({
          ...result,
          [seriesName]: mapSeries(rates[seriesName]),
        }),
        {},
      );
    },
    getSeriesColor(key) {
      const colors = {
        rates5years: '#1B8391',
        rates10years: '#E1A200',
        rates15years: '#E27265',
        rates20years: '#3BB9CB',
        bunds10yYields: '#695CC0',
      };

      return colors[key] ?? '#1B8391';
    },
    formatDataTooltip(point) {
      return this.yAxisFormatter.format(point);
    },
  },
};
